import type { IsoDate, IsoDateTime } from './common';
import type { MarketplaceChannel } from './marketplace';
import type { OrderStatus } from './orders';
import type { PutawayTaskView } from './inbound';

// Customer returns (müşteri iadeleri / RMA). A seller may announce a return with the code of
// the return parcel; the returns desk finds the original order by that code, the order number
// or the outbound tracking number, sorts every unit by condition and closes the return.

export const RETURN_STATUSES = ['expected', 'received', 'cancelled'] as const;
export type ReturnStatus = (typeof RETURN_STATUSES)[number];

export const RETURN_STATUS_LABELS: Readonly<Record<ReturnStatus, string>> = {
  expected: 'Bekleniyor',
  received: 'Teslim alındı',
  cancelled: 'İptal edildi',
};

export const RETURN_CONDITIONS = ['sellable', 'damaged', 'defective', 'opened'] as const;
export type ReturnCondition = (typeof RETURN_CONDITIONS)[number];

export const RETURN_CONDITION_LABELS: Readonly<Record<ReturnCondition, string>> = {
  sellable: 'Sağlam',
  damaged: 'Hasarlı',
  defective: 'Kusurlu',
  opened: 'Açılmış',
};

/**
 * Where the units go: sellable ones to the returns area and from there to a bin (directed
 * putaway); damaged and defective ones to the damaged area; opened ones to quarantine, for the
 * seller to decide. Only sellable units can be sold again.
 */
export const RETURN_CONDITION_AREAS: Readonly<Record<ReturnCondition, 'returns' | 'damaged' | 'quarantine'>> = {
  sellable: 'returns',
  damaged: 'damaged',
  defective: 'damaged',
  opened: 'quarantine',
};

/**
 * Geri al at the desk: the worker who received a return takes it back within this many minutes;
 * a supervisor later too. Either way only while none of its units were put away.
 */
export const RETURN_UNDO_MINUTES = 30;

/** Reasons the desk picks from for units that cannot be sold again (free text is allowed too). */
export const RETURN_DAMAGE_REASONS = ['Kırık / ezik', 'Ambalajı yırtık', 'Eksik parça', 'Kullanılmış', 'Lekeli / kirli', 'Etiketi yok', 'Yanlış ürün gönderilmiş'] as const;

/** An order line at the returns desk. */
export interface ReturnableItem {
  readonly orderItemId: string;
  readonly productId: string | null;
  readonly sku: string | null;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly barcodes: readonly { readonly code: string; readonly units: number }[];
  /** units that left in the parcel */
  readonly shipped: number;
  /** units already taken back on earlier returns */
  readonly returned: number;
  readonly returnable: number;
}

/** An order the desk found for a scanned code. */
export interface ReturnLookupMatch {
  readonly orderId: string;
  readonly orderNumber: string;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly channel: MarketplaceChannel;
  readonly status: OrderStatus;
  readonly customerName: string | null;
  /** KVKK: the buyer's name and address were erased then (the order is still found and taken back) */
  readonly personalDataErasedAt: IsoDateTime | null;
  readonly city: string | null;
  readonly shippedAt: IsoDateTime | null;
  readonly trackingNumber: string | null;
  readonly items: readonly ReturnableItem[];
  /** the seller's announcement for this order, if there is one */
  readonly expected: {
    readonly id: string;
    readonly rmaNumber: string;
    readonly returnTrackingNumber: string | null;
    readonly customerReason: string | null;
    readonly createdAt: IsoDateTime;
  } | null;
  /** why the order cannot be taken back here; null when it can */
  readonly problem: string | null;
}

export interface ReceiveReturnLine {
  readonly orderItemId: string;
  readonly qty: number;
  readonly condition: ReturnCondition;
  /** required unless sellable */
  readonly reason?: string | null;
}

export interface ReceiveReturnRequest {
  readonly warehouseId: string;
  readonly orderId: string;
  /** the code on the return parcel, when the desk scanned one */
  readonly returnTrackingNumber?: string | null;
  readonly note?: string | null;
  readonly lines: readonly ReceiveReturnLine[];
}

export interface CustomerReturnLineView {
  readonly sku: string;
  readonly name: string;
  readonly qty: number;
  readonly condition: ReturnCondition;
  readonly reason: string | null;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly locationCode: string;
}

export interface CustomerReturnView {
  readonly id: string;
  readonly rmaNumber: string;
  readonly status: ReturnStatus;
  readonly tenantCode: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly channel: MarketplaceChannel;
  readonly customerName: string | null;
  /** KVKK: the buyer's name was erased then */
  readonly personalDataErasedAt: IsoDateTime | null;
  readonly returnTrackingNumber: string | null;
  readonly carrierCode: string | null;
  readonly customerReason: string | null;
  readonly note: string | null;
  readonly createdAt: IsoDateTime;
  readonly receivedAt: IsoDateTime | null;
  readonly receivedBy: string | null;
  /** received by the person asking (the desk offers them geri al) */
  readonly receivedByMe: boolean;
  readonly lines: readonly CustomerReturnLineView[];
  readonly sellableUnits: number;
  readonly blockedUnits: number;
  /** sellable units already put away into a bin: the return can no longer be taken back */
  readonly storedUnits: number;
}

export interface ReceiveReturnResult {
  readonly return: CustomerReturnView;
  /** directed putaway of the sellable units, from the returns area */
  readonly putaway: readonly PutawayTaskView[];
  /** İade elleçleme ücreti booked to the seller, when the tariff has one */
  readonly charge: { readonly amount: string; readonly currency: string } | null;
  readonly orderStatus: OrderStatus;
}

/** A return taken back at the desk: its units left the books again, the parcel can be received anew. */
export interface UndoReturnResult {
  readonly return: CustomerReturnView;
  readonly orderStatus: OrderStatus;
  /** the İade elleçleme ücreti not invoiced yet was removed */
  readonly chargeRemoved: boolean;
}

export interface AnnounceReturnRequest {
  readonly orderId: string;
  readonly returnTrackingNumber?: string | null;
  readonly carrierCode?: string | null;
  readonly customerReason?: string | null;
}
